import Layout from 'components/layout/Layout'
import { serverSideTranslations } from 'next-i18next/pages/serverSideTranslations'
import i18nConfig from 'utils/i18nConfig'
import EncyclopediaArticle from 'features/encyclopedia/article/EncyclopediaArticle'
import { shuffleArray } from 'utils/helpers'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import type { SeoProps } from 'components/seo/types'
import { cmsLib } from 'lib'
import { mapCmsEncyclopediaArticlesToArticles, mapCmsEncyclopediaArticleToArticle } from 'utils/cms'
import { EncyclopediaArticleProps } from 'features/encyclopedia/article/types'

const EncyclopediaArticlePage = (props: EncyclopediaArticleProps) => {
  // Constants
  const { seo, description, title, thumbnail } = props.article
  const SEO: SeoProps = {
    description: seo?.metaDescription ?? description,
    image: seo?.seoImage?.url ?? thumbnail?.url,
    title: seo?.seoTitle ?? title
  }

  return (
    <Layout seo={SEO}>
      <EncyclopediaArticle {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  const { article: slug } = params

  // Fetch the data
  const [article, encyclopediaArticles] = await Promise.all([
    cmsLib.getEncyclopediaArticleBySlug(slug),
    cmsLib.getEncyclopediaArticles(locale)
  ])

  const mappedArticle = article ? mapCmsEncyclopediaArticleToArticle(article) : null

  if (!mappedArticle) {
    return {
      notFound: true,
      revalidate: 5
    }
  }

  // Convert the articles (for filtering & using in the components)
  const articles = mapCmsEncyclopediaArticlesToArticles(encyclopediaArticles)

  // Shuffle the articles
  const shuffledArticles = shuffleArray(articles)

  return {
    props: {
      article: mappedArticle,
      allArticles: shuffledArticles,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'blog', 'encyclopedia', 'faqIncreaseInstallment'], i18nConfig))
    }
  }
}

export const getStaticPaths = async () => {
  const paths: { locale: string; params: { article: string; category: string } }[] = []
  const encyclopediaArticles = await cmsLib.getEncyclopediaArticles()
  const articles = mapCmsEncyclopediaArticlesToArticles(encyclopediaArticles)

  articles.forEach((article) => {
    const articlePaths = [{ locale: 'nl', params: { article: article.slug, category: article.category.slug } }]
    paths.push(...articlePaths)
  })

  return {
    fallback: 'blocking',
    paths // will server-render pages on-demand if the path does not exist
  }
}

export default EncyclopediaArticlePage
